import dateFormat from "dateformat";
import isEmpty from "is-empty";
import moment, { Moment } from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { updatePrimaryOccupantMutation } from "rsl-api-106/lib/mutations";
import {
  Maybe,
  Mutation,
  MutationUpdatePrimaryOccupantArgs,
  PrimaryOccupant,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import InputText from "@components/_Layout/Controls/InputText";
import RadioCivility from "@components/_Layout/Controls/RadioCivility";
import SDPicker from "@components/_Layout/Controls/SDPicker";
import SelectCivility from "@components/_Layout/Controls/SelectCivility";
import { useCart, useSession } from "@hooks";

type PartnerDetailsProps = {
  user?: PrimaryOccupant | null;
  onEditSuccess?: () => void;
  editIcon?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_updating_account_in_progress", //Mise à jour de votre profil en cours
  "LANG_neobooking_account_successfully_updated", //Votre compte a bien été modifié !
  "LANG_neobooking_civility", //Civilité
  "LANG_neobooking_lastname", //Nom
  "LANG_neobooking_firstname", //Prénom
  "LANG_neobooking_city", //Ville
  "LANG_neobooking_adr_street", //N° et voie
  "LANG_neobooking_adr_zipcode", //Code postal
  "LANG_neobooking_adr_tel_1", //Téléphone 1
  "LANG_neobooking_confirm_my_information", //Valider mes modifications
  "LANG_neobooking_editing", //Modification
];

const PartnerDetails: React.FC<PartnerDetailsProps> = ({
  user,
  editIcon,
  onEditSuccess,
}) => {
  /* Hooks */
  const { sessionId, getLanguageVarValue } = useSession();
  const { setCart } = useCart();
  const client = useApolloClient();

  /* Loading State */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /* User State */
  const [email, setEmail] = useState<Maybe<string> | undefined>(
    user?.address?.mail
  );

  const [password, setPassword] = useState<string>("*********");
  const [passwordConfirm, setPasswordConfirm] = useState<string>("*********");

  const [lastname, setLastname] = useState<Maybe<string> | undefined>(
    user?.lastName ?? ""
  );
  const [firstname, setFirstname] = useState<Maybe<string> | undefined>(
    user?.firstName ?? ""
  );

  const [birthdate, setBirthdate] = useState<Moment | null>(
    user?.birthDate ? moment(new Date(user.birthDate)) : null
  );
  const [language, setLanguage] = useState<Maybe<string> | undefined>(
    user?.occupantLanguage?.code
  );
  const [civility, setCivility] = useState<Maybe<string> | undefined>(
    user?.civility?.code
  );
  const [country, setCountry] = useState<Maybe<string> | undefined>(
    user?.address?.addressCountry?.code
  );

  const [streetName, setStreetName] = useState<Maybe<string> | undefined>(
    user?.address?.streetName
  );
  const [postalLocality, setPostalLocality] = useState<
    Maybe<string> | undefined
  >(user?.address?.postalLocality);
  const [addressComplementary1, setAddressComplementary1] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary1);
  const [addressComplementary2, setAddressComplementary2] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary2);
  const [zipCode, setZipCode] = useState<Maybe<string> | undefined>(
    user?.address?.zipCode
  );
  const [city, setCity] = useState<Maybe<string> | undefined>(
    user?.address?.city
  );
  const [phone1, setPhone1] = useState<Maybe<string> | undefined>(
    user?.address?.phone1
  );
  const [phone2, setPhone2] = useState<Maybe<string> | undefined>(
    user?.address?.phone2
  );

  useEffect(() => {
    if (user) {
      setEmail(user?.address?.mail);
      setLastname(user.lastName ?? "");
      setFirstname(user.firstName ?? "");
      setLanguage(user.occupantLanguage?.code);
      setCivility(user.civility?.code);
      setCountry(user.address?.addressCountry?.code);
      setStreetName(user.address?.streetName);
      setPostalLocality(user.address?.postalLocality);
      setAddressComplementary1(user.address?.addressComplementary1);
      setAddressComplementary2(user.address?.addressComplementary2);
      setZipCode(user.address?.zipCode);
      setCity(user.address?.city);
      setPhone1(user.address?.phone1);
      setPhone2(user.address?.phone2);
      setBirthdate(
        user?.birthDate
          ? moment(new Date(user.birthDate))
          : moment().add(-30, "years")
      );
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      (firstname && firstname.trim().length <= 1) ||
      (lastname && lastname.trim().length <= 1)
    ) {
      toast.error("LANG_neobooking_register_invalid_lastname_firstname");
      return;
    }

    setIsSubmitting(true);
    const toastEdit = toast.loading(
      `${getLanguageVarValue(
        "LANG_neobooking_updating_account_in_progress"
      )}...`
    );

    const formatedBirthdate = dateFormat(birthdate?.toDate(), "yyyy-mm-dd");

    client
      .mutate<Mutation, MutationUpdatePrimaryOccupantArgs>({
        mutation: updatePrimaryOccupantMutation(),
        fetchPolicy: "no-cache",
        variables: {
          session: { name: sessionId },
          input: {
            civility: civility,
            firstName: firstname ?? "",
            lastName: lastname ?? "",
            birthDate: formatedBirthdate,
            languageCode: language,
            bookingId: undefined,
            address: {
              streetName: streetName,
              postalLocality: postalLocality,
              addressComplementary1: addressComplementary1,
              addressComplementary2: addressComplementary2,
              zipCode: zipCode,
              city: city,
              country: country,
              phone1: phone1,
              phone2: phone2,
              mail: email ?? "",
            },
          },
        },
      })
      .catch((err) => {
        toast.error(err.message, {
          id: toastEdit,
        });
        console.log(err);
      })
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.updatePrimaryOccupant &&
          response.data.updatePrimaryOccupant.cart
        ) {
          setCart(response.data.updatePrimaryOccupant.cart);
          toast.success(
            getLanguageVarValue("LANG_neobooking_account_successfully_updated"),
            {
              id: toastEdit,
            }
          );
          onEditSuccess && onEditSuccess();
        } else {
          toast.dismiss(toastEdit);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <div className="tw-titre-principal-spirit">
        {getLanguageVarValue("LANG_neobooking_partner_details")}
      </div>
      <form onSubmit={handleSubmit}>
        <div className="tw-my-6">
          <div className="tw-my-4">
            <div className="tw-grid tw-grid-cols-12 tw-gap-6">
              <div className="tw-col-span-12">
                <div className="tw-block sm:tw-hidden">
                  <SelectCivility
                    value={civility!!}
                    onChange={(e) => setCivility(e.target.value)}
                    additionalClassname="tw-w-full"
                  />
                </div>
                <div className="tw-hidden sm:tw-block">
                  <div className="tw-mb-2">
                    {getLanguageVarValue("LANG_neobooking_civility")}
                  </div>
                  <RadioCivility
                    value={civility!!}
                    onChange={(e) => setCivility(e.target.value)}
                  />
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon={editIcon}
                  required
                  placeholder={`${getLanguageVarValue(
                    "LANG_neobooking_lastname"
                  )}*`}
                  value={lastname!!}
                  onChange={(e) => setLastname(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon={editIcon}
                  required
                  placeholder={`${getLanguageVarValue(
                    "LANG_neobooking_firstname"
                  )}*`}
                  value={firstname!!}
                  onChange={(e) => setFirstname(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <SDPicker
                  isRequired={true}
                  selectedSingleDate={birthdate}
                  setSelectedSingleDate={setBirthdate}
                />
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <InputText
                  editIcon={editIcon}
                  required
                  placeholder={`${getLanguageVarValue(
                    "LANG_neobooking_adr_tel_1"
                  )}*`}
                  value={phone1!!}
                  onChange={(e) => setPhone1(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12">
                <InputText
                  editIcon={editIcon}
                  value={streetName!!}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_adr_street"
                  )}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
              <div className="tw-col-span-12">
                <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                  <div className="tw-col-span-6">
                    <InputText
                      editIcon={editIcon}
                      placeholder={getLanguageVarValue(
                        "LANG_neobooking_adr_zipcode"
                      )}
                      value={zipCode!!}
                      onChange={(e) => setZipCode(e.target.value)}
                      additionalClassname="tw-w-full"
                      type="text"
                    />
                  </div>
                  <div className="tw-col-span-6">
                    <InputText
                      editIcon={editIcon}
                      placeholder={getLanguageVarValue("LANG_neobooking_city")}
                      value={city!!}
                      onChange={(e) => setCity(e.target.value)}
                      additionalClassname="tw-w-full"
                      type="text"
                    />
                  </div>
                </div>
              </div>
              <div className="tw-col-span-12">
                <InputText
                  editIcon={editIcon}
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_email_address"
                  )}
                  value={email!!}
                  onChange={(e) => setEmail(e.target.value)}
                  additionalClassname="tw-w-full"
                  type="text"
                />
              </div>
            </div>
          </div>
          <Button
            type="submit"
            variant="gradient"
            additionalClassname="tw-font-extrabold tw-capitalize"
            loadingChildren={
              <>{getLanguageVarValue("LANG_neobooking_editing")}...</>
            }
            disabled={
              password !== passwordConfirm ||
              (isEmpty(password) && isEmpty(passwordConfirm)) ||
              isSubmitting
            }
            isLoading={isSubmitting}
          >
            {getLanguageVarValue("LANG_neobooking_confirm_my_information")}
          </Button>
        </div>
      </form>
    </>
  );
};

PartnerDetails.propTypes = {
  //
};

export default PartnerDetails;
